// Vue-powered replacements for theme jQuery behaviors
// Provides: sticky header events, simple lightbox, sticky sidebar

(() => {
  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  function dispatch(name, detail) {
    try {
      const evt = new CustomEvent(name, { detail });
      document.dispatchEvent(evt);
    } catch (_) {
      const evt = document.createEvent('Event');
      evt.initEvent(name, true, true);
      document.dispatchEvent(evt);
    }
  }

  const getCSSVarInt = (el, name, fallback = 0) => {
    const v = window.getComputedStyle(el).getPropertyValue(name);
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  // Sticky Header
  function initStickyHeader(selector, { mobile = false } = {}) {
    const header = document.querySelector(selector);
    if (!header) return;

    const rows = Array.from(header.querySelectorAll('.smart-head-row'));
    if (!rows.length) return;
    const stickyTarget = rows[rows.length - 1];

    let stickyTop = 0;
    let isSticky = false;
    let prevWidth = 0;
    let lastScrollY = 0;
    let scrollDirection = 'down';
    let isHidden = false;

    const calc = () => {
      const rect = stickyTarget.getBoundingClientRect();
      stickyTop = rect.top + window.scrollY;
    };

    const apply = () => {
      const shouldEnable = mobile ? window.innerWidth <= 940 : window.innerWidth > 0;
      if (!shouldEnable) {
        if (isSticky) {
          stickyTarget.classList.remove('smart-head-sticky', 'off');
          isSticky = false;
          isHidden = false;
          dispatch('sticky-bar-hide');
        }
        return;
      }
      
      const cur = window.scrollY;
      const nowSticky = cur > stickyTop;
      
      // 检测滚动方向
      if (cur > lastScrollY) {
        scrollDirection = 'down';
      } else if (cur < lastScrollY) {
        scrollDirection = 'up';
      }
      lastScrollY = cur;
      
      if (nowSticky && !isSticky) {
        stickyTarget.classList.add('smart-head-sticky');
        isSticky = true;
        isHidden = false;
        dispatch('sticky-bar-show');
      } else if (!nowSticky && isSticky) {
        stickyTarget.classList.remove('smart-head-sticky', 'off');
        isSticky = false;
        isHidden = false;
        dispatch('sticky-bar-hide');
      } else if (isSticky) {
        // 当菜单已经粘性显示时，根据滚动方向控制显示/隐藏
        if (scrollDirection === 'up' && !isHidden) {
          // 向上滚动时隐藏菜单
          stickyTarget.classList.add('off');
          isHidden = true;
          dispatch('sticky-bar-hide');
        } else if (scrollDirection === 'down' && isHidden) {
          // 向下滚动时显示菜单
          stickyTarget.classList.remove('off');
          isHidden = false;
          dispatch('sticky-bar-show');
        }
      }
    };

    const onScroll = () => {
      window.requestAnimationFrame(apply);
    };

    const onResize = () => {
      if (window.innerWidth !== prevWidth) {
        prevWidth = window.innerWidth;
        calc();
        apply();
      }
    };

    calc();
    apply();
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  }

  // Simple Lightbox for images
  function initLightbox(root = document) {
    const sel = '.entry-content a[href$=".jpg"], .entry-content a[href$=".jpeg"], .entry-content a[href$=".png"], .entry-content a[href$=".webp"], .entry-content a[href$=".gif"]';
    const links = root.querySelectorAll(sel);
    if (!links.length) return;

    let overlay;
    const ensureOverlay = () => {
      if (overlay) return overlay;
      overlay = document.createElement('div');
      overlay.className = 'vue-lightbox-overlay';
      Object.assign(overlay.style, {
        position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.85)', display: 'none', alignItems: 'center', justifyContent: 'center', zIndex: '9999'
      });
      const img = document.createElement('img');
      img.style.maxWidth = '95%';
      img.style.maxHeight = '95%';
      overlay.appendChild(img);
      overlay.addEventListener('click', () => hide());
      document.body.appendChild(overlay);
      window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
      return overlay;
    };
    const show = (src) => {
      const ov = ensureOverlay();
      ov.querySelector('img').src = src;
      ov.style.display = 'flex';
      document.documentElement.style.overflow = 'hidden';
    };
    const hide = () => {
      if (!overlay) return;
      overlay.style.display = 'none';
      document.documentElement.style.overflow = '';
    };
    links.forEach((a) => {
      a.addEventListener('click', (e) => {
        // Only lightbox if clicking image links
        e.preventDefault();
        const href = a.getAttribute('href');
        if (href) show(href);
      });
    });
  }

  // Sticky Sidebar with position: sticky fallback
  function initStickySidebar() {
    const sidebars = Array.from(document.querySelectorAll('.main-sidebar[data-sticky="1"], .ts-sticky-col'));
    if (!sidebars.length) return;
    const adminBar = document.querySelector('#wpadminbar');
    const baseTop = 20 + (adminBar ? adminBar.offsetHeight : 0);
    const setTop = (px) => {
      document.body.style.setProperty('--ts-sticky-top', `${px}px`);
      sidebars.forEach((sb) => {
        const inner = sb.querySelector('.theiaStickySidebar') || sb;
        inner.style.position = 'sticky';
        inner.style.top = `var(--ts-sticky-top, ${px}px)`;
      });
    };
    setTop(baseTop);
    document.addEventListener('sticky-bar-show', () => {
      const stickyHead = document.querySelector('.smart-head-sticky');
      const add = stickyHead ? getCSSVarInt(stickyHead, '--head-h', 0) : 0;
      setTop(baseTop + add);
    });
    document.addEventListener('sticky-bar-hide', () => setTop(baseTop));
  }

  function reInit(root = document) {
    try { initLightbox(root); } catch (_) {}
    try { initSliders(root); } catch (_) {}
    try { initMegaMenus(); } catch (_) {}
  }

  function initAll() {
    // Provide global Bunyad object compatibility
    window.Bunyad = window.Bunyad || {};
    window.Bunyad.theme = window.Bunyad.theme || {};
    window.Bunyad.theme.stickyBarPause = false;
    window.Bunyad.theme.reInit = reInit;

    initStickyHeader('.smart-head-main', { mobile: false });
    initStickyHeader('.smart-head-mobile', { mobile: true });
    initStickySidebar();
    initLightbox(document);
    initMobileMenu();
    initSliders();
    initMegaMenus();
  }

  onReady(initAll);
})();

// --- Mobile Menu (Off-Canvas) ---
function initMobileMenu() {
  try {
    const html = document.documentElement;
    const container = document.querySelector('.mobile-menu-container.off-canvas');
    if (!container) return;

    const openToggles = document.querySelectorAll('.offcanvas-toggle');
    const closeBtn = container.querySelector('.off-canvas-head .close');
    const mobileMenu = container.querySelector('.mobile-menu');
    let initialized = false;

    const buildMobileMenu = () => {
      if (initialized) return;
      const mainMenu = document.querySelector('.navigation-main .menu');
      if (!mainMenu) return;
      // Clone main menu structure
      mobileMenu.innerHTML = mainMenu.innerHTML;
      // Flatten simple mega menu structures: move nested .sub-nav into ULs
      mobileMenu.querySelectorAll('.mega-menu .sub-cats').forEach((node) => {
        const container = node.closest('.menu-item');
        if (!container) return;
        const sub = node.querySelector('.sub-nav');
        if (sub) {
          const ul = document.createElement('ul');
          ul.innerHTML = sub.innerHTML;
          container.appendChild(ul);
        }
        node.remove();
      });
      // Add chevrons for items with children
      mobileMenu.querySelectorAll('li > a').forEach((a) => {
        const li = a.parentElement;
        if (li && li.querySelector(':scope > ul')) {
          const span = document.createElement('span');
          span.className = 'chevron';
          span.innerHTML = '<i class="tsi tsi-chevron-down"></i>';
          span.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            li.classList.toggle('active');
            li.classList.toggle('item-active');
          });
          a.insertAdjacentElement('afterend', span);
        }
      });
      initialized = true;
    };

    const toggleMenu = () => {
      if (!initialized) buildMobileMenu();
      html.classList.toggle('off-canvas-active');
    };

    openToggles.forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); toggleMenu(); }));
    if (closeBtn) closeBtn.addEventListener('click', (e) => { e.preventDefault(); toggleMenu(); });
    // Click outside content to close (optional if backdrop exists)
    container.addEventListener('click', (e) => {
      const content = container.querySelector('.off-canvas-content');
      if (!content) return;
      if (!content.contains(e.target) && html.classList.contains('off-canvas-active')) {
        toggleMenu();
      }
    });
  } catch (_) {}
}

// --- Simple Slider Engine ---
function initSliders(root = document) {
  try {
    const selectors = ['.common-slider .slides:not(.loaded)', '.loop-carousel:not(.loaded)', '.gallery-slider:not(.loaded)'];
    const nodes = root.querySelectorAll(selectors.join(','));
    nodes.forEach((slider) => {
      if (slider.classList.contains('loaded')) return;
      makeSlider(slider);
      slider.classList.add('loaded');
    });

    // Classic slider (Flexslider replacement)
    const classicFlex = root.querySelectorAll('.classic-slider .flexslider:not(.loaded)');
    classicFlex.forEach((wrap) => {
      const ul = wrap.querySelector('.slides');
      if (!ul) return;
      makeSlider(ul);
      wrap.classList.add('loaded');
    });
  } catch (_) {}
}

function makeSlider(slider) {
  const type = slider.getAttribute('data-slider') || (slider.classList.contains('gallery-slider') ? 'gallery' : 'carousel');
  const autoplay = slider.getAttribute('data-autoplay') != null;
  const speed = parseInt(slider.getAttribute('data-speed') || '5000', 10);
  const dotsEnabled = slider.getAttribute('data-dots') != null;
  const arrowsEnabled = slider.getAttribute('data-arrows') != null;
  const slides = slider.children;
  if (!slides || !slides.length) return;

  // Track element can be the slider itself (e.g., ul.slides) to preserve CSS
  let track;
  if (slider.tagName === 'UL') {
    track = slider;
  } else {
    track = document.createElement('div');
    track.className = 'vue-slider-track';
    const slideEls = Array.from(slider.children);
    slideEls.forEach((el) => track.appendChild(el));
    slider.appendChild(track);
  }
  track.style.display = 'flex';
  track.style.willChange = 'transform';
  track.style.transition = 'transform 400ms ease-out';
  slider.style.overflow = 'hidden';

  // Arrows
  const controlsHost = (slider.parentElement && slider.parentElement.classList && slider.parentElement.classList.contains('flexslider')) ? slider.parentElement : slider;
  let prevBtn, nextBtn;
  if (arrowsEnabled) {
    prevBtn = controlsHost.querySelector('.prev-arrow') || createArrow('prev');
    nextBtn = controlsHost.querySelector('.next-arrow') || createArrow('next');
    controlsHost.appendChild(prevBtn);
    controlsHost.appendChild(nextBtn);
  }

  // Dots
  let dots;
  if (dotsEnabled) {
    dots = document.createElement('ul');
    dots.className = 'nav-dots';
    controlsHost.appendChild(dots);
  }

  let current = 0;
  let visible = 1;
  const dataSlides = parseInt(slider.getAttribute('data-slides') || '1', 10);
  const dataSlidesMd = parseInt(slider.getAttribute('data-slides-md') || Math.min(4, Math.max(2, dataSlides - 1)), 10);
  const dataSlidesSm = parseInt(slider.getAttribute('data-slides-sm') || '1', 10);

  const updateVisible = () => {
    const w = window.innerWidth;
    visible = type === 'gallery' ? 1 : (w < 540 ? dataSlidesSm : (w < 940 ? dataSlidesMd : dataSlides));
    Array.from(track.children).forEach((child) => {
      child.style.flex = `0 0 ${100 / visible}%`;
      child.style.maxWidth = `${100 / visible}%`;
    });
    update();
    buildDots();
  };

  const pageCount = () => Math.max(1, Math.ceil(track.children.length / visible));

  const update = () => {
    if (current >= pageCount()) current = pageCount() - 1;
    const offsetPct = -(current * 100);
    track.style.transform = `translateX(${offsetPct}%)`;
    if (dots) {
      Array.from(dots.children).forEach((li, i) => li.classList.toggle('active', i === current));
    }
  };

  const buildDots = () => {
    if (!dots) return;
    dots.innerHTML = '';
    for (let i = 0; i < pageCount(); i++) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#';
      a.addEventListener('click', (e) => { e.preventDefault(); current = i; update(); });
      li.appendChild(a);
      if (i === current) li.classList.add('active');
      dots.appendChild(li);
    }
  };

  function createArrow(dir) {
    const a = document.createElement('a');
    a.href = '#';
    a.className = dir === 'prev' ? 'prev-arrow' : 'next-arrow';
    a.innerHTML = dir === 'prev' ? '<i class="tsi tsi-angle-left"></i>' : '<i class="tsi tsi-angle-right"></i>';
    a.addEventListener('click', (e) => {
      e.preventDefault();
      if (dir === 'prev') current = Math.max(0, current - 1);
      else current = Math.min(pageCount() - 1, current + 1);
      update();
    });
    return a;
  }

  let timer;
  const resetAutoplay = () => {
    if (!autoplay) return;
    if (timer) clearInterval(timer);
    timer = setInterval(() => {
      current = (current + 1) % pageCount();
      update();
    }, Math.max(1500, speed));
  };

  window.addEventListener('resize', () => { updateVisible(); resetAutoplay(); });
  updateVisible();
  resetAutoplay();
}

// --- Desktop Mega Menus ---
function initMegaMenus() {
  try {
    const media = window.matchMedia('(min-width: 940px)');
    const setupAll = () => {
      document.querySelectorAll('.navigation .mega-menu-a').forEach((menu) => {
        if (menu.dataset.vmegaInit === '1') return;
        menu.dataset.vmegaInit = '1';

        const resetDefault = () => {
          menu.querySelectorAll('.active, .current-menu-item').forEach((el) => el.classList.remove('active', 'current-menu-item'));
          const posts = menu.querySelectorAll('.posts');
          const def = posts[posts.length - 1] || posts[0];
          if (def) def.classList.add('active');
        };

        const parentLi = menu.closest('li') || menu.parentElement;
        if (parentLi) parentLi.addEventListener('mouseenter', resetDefault);

        const items = menu.querySelectorAll('.sub-cats .menu-item');
        items.forEach((li) => {
          li.addEventListener('mouseenter', () => {
            const m = (li.className || '').match(/menu-cat-(\d+)/);
            const id = m ? m[1] : null;
            const isViewAll = li.classList.contains('view-all');
            if (!id && !isViewAll) return;
            menu.querySelectorAll('.active, .current-menu-item').forEach((el) => el.classList.remove('active', 'current-menu-item'));
            li.classList.add('current-menu-item');
            if (id) {
              const target = menu.querySelector(`[data-id="${id}"]`);
              if (target) target.classList.add('active');
            }
          });
        });
      });
    };
    const handler = (e) => { if (e.matches) setupAll(); };
    handler(media);
    if (media.addEventListener) media.addEventListener('change', handler); else media.addListener(handler);
  } catch (_) {}
}
